import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaArchive, FaBookOpen, FaDownload, FaEdit, FaEye, FaFileAlt, FaPlus, FaRegStar, FaStar, FaSlidersH,
} from "react-icons/fa";
import { COURSES_CONSTANTS } from "../_MODULES/_API";
import { CourseFormModal } from "../_UI/CourseFormModal";
import { CourseReadModal } from "../_UI/CourseReadModal";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { EmptyState, Skeleton } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { downloadCourseMarkdown, downloadCoursePdf } from "../_UI/courseExport.helpers";
import { usePagedList } from "../../../../_UI/usePagedList";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { REACT_HOT_TOAST_ERROR } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";
import "../_UI/_Styles/Courses.css";

const STATUS_LABEL = {
    IDEA: "Idea",
    DRAFT: "Borrador",
    PLANNING: "Planificando",
    READY: "Preparado",
    ACTIVE: "En curso",
    PAUSED: "Pausado",
    COMPLETED: "Finalizado",
};

const LEVEL_LABEL = {
    BEGINNER: "Inicial",
    BASIC: "Basico",
    INTERMEDIATE: "Intermedio",
    ADVANCED: "Avanzado",
    ALL_LEVELS: "Mixto",
};

const STATUS_OPTIONS = [
    { value: "", label: "Todos los estados" },
    { value: "IDEA", label: "Idea" },
    { value: "PLANNING", label: "Planificando" },
    { value: "READY", label: "Preparado" },
    { value: "ACTIVE", label: "En curso" },
    { value: "PAUSED", label: "Pausado" },
    { value: "COMPLETED", label: "Finalizado" },
];

const LEVEL_OPTIONS = [
    { value: "", label: "Todos los niveles" },
    { value: "BEGINNER", label: "Inicial" },
    { value: "BASIC", label: "Basico" },
    { value: "INTERMEDIATE", label: "Intermedio" },
    { value: "ADVANCED", label: "Avanzado" },
    { value: "ALL_LEVELS", label: "Mixto" },
];

const formatDate = (value) => value
    ? new Date(value).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" })
    : "-";

export const CoursesViewMain = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [level, setLevel] = useState("");
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ favorite: "ALL", category: "", platform: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const [isOpen, setIsOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [reading, setReading] = useState(null);
    const [exportingId, setExportingId] = useState("");

    const {
        apiGet,
        useApiData: COURSES,
        useApiLoading: IS_LOADING,
        mutateLocal,
    } = useApiQuery({ minTimeQuery: 200, enablePagination: false, startLoading: true });

    const { apiGet: getCourseForExport } = useApiQuery({
        minTimeQuery: 120, enablePagination: false, startLoading: false,
    });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true,
        isToastSuccess: true,
        minTimeMutate: 0,
    });

    useEffect(() => {
        apiGet({ endpoint: COURSES_CONSTANTS.LIST });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        return (Array.isArray(COURSES) ? COURSES : []).filter((course) => {
            const matchesSearch = !query || [
                course.title,
                course.description,
                course.category,
                course.platform,
                STATUS_LABEL[course.status],
                LEVEL_LABEL[course.level],
            ].filter(Boolean).join(" ").toLowerCase().includes(query);
            const matchesStatus = !status || course.status === status;
            const matchesLevel = !level || course.level === level;
            const matchesFavorite = advanced.favorite === "ALL" || (!!course.isFavorite === (advanced.favorite === "YES"));
            const matchesCategory = !String(advanced.category || "").trim() || onMatchesSearch(advanced.category, [course.category]);
            const matchesPlatform = !String(advanced.platform || "").trim() || onMatchesSearch(advanced.platform, [course.platform]);
            const matchesDate = onDateMatches(advanced.dateField === "CREATED" ? course.createdAt : course.updatedAt, advanced.dateRange, advanced.dateExact);
            return matchesSearch && matchesStatus && matchesLevel && matchesFavorite && matchesCategory && matchesPlatform && matchesDate;
        });
    }, [COURSES, search, status, level, advanced]);
    const advancedCount = [status, level, advanced.favorite !== "ALL", String(advanced.category || "").trim(), String(advanced.platform || "").trim(), advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact].filter(Boolean).length;
    const clearAdvanced = () => {
        setStatus("");
        setLevel("");
        setAdvanced({ favorite: "ALL", category: "", platform: "", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    };

    const PAGER = usePagedList(filtered, 6, "page");

    const onSave = async (values) => {
        const isEdit = !!editing?.id;
        const previous = isEdit ? { ...editing } : null;
        if (isDuplicateRecord({ rows: Array.isArray(COURSES) ? COURSES : [], value: values.title, currentId: editing?.id, getValue: (row) => row?.title })) {
            REACT_HOT_TOAST_ERROR("Ya existe un curso con ese título");
            return false;
        }

        if (isEdit) mutateLocal({ id: editing.id, patch: values });

        const response = await actionMutate({
            method: isEdit ? "PATCH" : "POST",
            endpoint: isEdit ? COURSES_CONSTANTS.UPDATE : COURSES_CONSTANTS.CREATE,
            id: isEdit ? editing.id : null,
            data: values,
            successMessage: isEdit ? "Curso actualizado" : "Curso creado",
        });

        if (!response.success) {
            if (previous) mutateLocal({ id: previous.id, patch: previous });
            return response;
        }

        const saved = response?.data?.data || response?.data;
        if (saved?.id) {
            if (isEdit) mutateLocal({ id: saved.id, patch: saved });
            else mutateLocal({
                insert: {
                    ...saved,
                    moduleCount: 0,
                    lessonCount: 0,
                    taskCount: 0,
                    pendingTaskCount: 0,
                },
            });
        }

        setEditing(null);
        if (!isEdit && saved?.id) window.setTimeout(() => navigate(`/courses/${saved.id}/notebook`), 0);
        return true;
    };

    const onFavorite = async (course) => {
        const next = !course.isFavorite;
        mutateLocal({ id: course.id, patch: { isFavorite: next } });

        const response = await actionMutate({
            method: "PATCH",
            endpoint: COURSES_CONSTANTS.UPDATE,
            id: course.id,
            data: { isFavorite: next },
            successMessage: next ? "Curso fijado" : "Curso quitado de favoritos",
        });

        if (!response.success) mutateLocal({ id: course.id, patch: { isFavorite: !next } });
    };

    const onArchive = async (course) => {
        const accepted = await confirm.danger(
            "Archivar curso",
            `Se archiva "${course.title}" con todos sus apuntes, tareas e itinerario. Lo podes recuperar desde Archivados.`,
        );
        if (!accepted) return;

        mutateLocal({ id: course.id, remove: true });
        const response = await onApiAction({
            method: "PATCH",
            endpoint: COURSES_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: course.id },
            successMessage: "Curso archivado",
        });
        if (!response.success) mutateLocal({ insert: course });
    };

    const onExportCourse = async (course, format) => {
        if (!course?.id || exportingId) return;
        setExportingId(course.id);
        try {
            const response = await getCourseForExport({
                endpoint: COURSES_CONSTANTS.DETAIL,
                isDetail: true,
                config: { pathParams: { id: course.id } },
            });
            if (!response?.success || !response?.data) return;
            if (format === "pdf") downloadCoursePdf(response.data);
            if (format === "markdown") downloadCourseMarkdown(response.data);
        } finally {
            setExportingId("");
        }
    };

    return (
        <ViewLayout
            header={
                <PageHeader
                    title="Cursos"
                    icon={<FaBookOpen />}
                    description="Cuadernos privados para preparar clases, ordenar temas, guardar apuntes, tareas e itinerarios."
                    actions={
                        <AppButton icon={<FaPlus />} onClick={() => { setEditing(null); setIsOpen(true); }}>
                            Nuevo curso
                        </AppButton>
                    }
                />
            }
            toolbar={
                <ViewToolbar
                    filters={<>
                        <SearchFilter value={search} onChange={setSearch} total={Array.isArray(COURSES) ? COURSES.length : 0} matches={filtered.length}
                            placeholder="Buscar por curso, tema o descripcion..." />
                        <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />} onClick={() => setAdvancedOpen((current) => !current)}>{advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}</AppButton>
                    </>}
                    expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                        <FieldSelect id="course-status" label="Estado" value={status} onChange={(event) => setStatus(event.target.value)} options={STATUS_OPTIONS} />
                        <FieldSelect id="course-level" label="Nivel" value={level} onChange={(event) => setLevel(event.target.value)} options={LEVEL_OPTIONS} />
                        <FieldSelect id="courses_adv_favorite" label="Destacado" value={advanced.favorite} onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
                        <FieldInput id="courses_adv_category" label="Categoría" value={advanced.category} onChange={(event) => setAdvanced((current) => ({ ...current, category: event.target.value }))} />
                        <FieldInput id="courses_adv_platform" label="Plataforma" value={advanced.platform} onChange={(event) => setAdvanced((current) => ({ ...current, platform: event.target.value }))} />
                        <FieldSelect id="courses_adv_date" label="Fecha" value={advanced.dateField} onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))} options={[{ value: "UPDATED", label: "Última actualización" }, { value: "CREATED", label: "Creación" }]} />
                        <FieldDate id="courses_adv_from" label="Desde" value={advanced.dateRange?.[0] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                        <FieldDate id="courses_adv_to" label="Hasta" value={advanced.dateRange?.[1] || null} onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                        <FieldDate id="courses_adv_exact" label="Fecha exacta" value={advanced.dateExact} onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                    </AdvancedFilterPanel>}
                    total={PAGER.total}
                    perPage={PAGER.usePerPage}
                    onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={[
                        { key: "title", label: "Curso" },
                        { key: "status", label: "Estado" },
                        { key: "level", label: "Nivel" },
                        { key: "modules", label: "Modulos" },
                        { key: "notes", label: "Apuntes" },
                        { key: "tasks", label: "Tareas pendientes" },
                    ]}
                    exportRows={filtered.map((course) => ({
                        title: course.title,
                        status: STATUS_LABEL[course.status] || course.status,
                        level: LEVEL_LABEL[course.level] || course.level,
                        modules: course.moduleCount || 0,
                        notes: course.lessonCount || 0,
                        tasks: course.pendingTaskCount || 0,
                    }))}
                    exportName="teaching-courses"
                />
            }
        >
            {IS_LOADING && (
                <div className="LStudio__courses_library">
                    <Skeleton variant="box" height={96} count={3} />
                </div>
            )}

            {!IS_LOADING && filtered.length === 0 && (
                <EmptyState
                    icon={<FaBookOpen />}
                    title={search || status || level ? "No encontramos cursos" : "Todavia no hay cursos"}
                    description={search || status || level
                        ? "Proba cambiando los filtros."
                        : "Crea un curso para guardar lo que queres ensenar, preparar y recordar."}
                    action={!search && !status && !level
                        ? <AppButton icon={<FaPlus />} onClick={() => setIsOpen(true)}>Crear primer curso</AppButton>
                        : null}
                />
            )}

            {!IS_LOADING && filtered.length > 0 && (
                <div className="LStudio__courses_library">
                    {PAGER.paged.map((course) => (
                        <div
                            key={course.id}
                            className="LStudio__courses_library_row"
                            role="button"
                            tabIndex={0}
                            onClick={() => setReading(course)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") setReading(course);
                            }}
                        >
                            <div className="LStudio__courses_library_book">
                                <FaBookOpen />
                            </div>

                            <div className="LStudio__courses_library_main">
                                <div className="LStudio__courses_library_titleline">
                                    <strong>{course.title}</strong>
                                    {course.isFavorite && <FaStar className="LStudio__courses_library_favorite" />}
                                </div>
                                <p>{course.description || "Sin descripcion. Entra y empeza a guardar tus apuntes docentes."}</p>
                                <div className="LStudio__courses_library_meta">
                                    <span>{STATUS_LABEL[course.status] || course.status || "Sin estado"}</span>
                                    <span>{LEVEL_LABEL[course.level] || course.level || "Sin nivel"}</span>
                                    <span>{course.moduleCount || 0} modulos</span>
                                    <span>{course.lessonCount || 0} apuntes</span>
                                    <span>{course.pendingTaskCount || 0} tareas pendientes</span>
                                    <span>Actualizado {formatDate(course.updatedAt)}</span>
                                </div>
                            </div>

                            <div className="LStudio__courses_library_actions" onClick={(event) => event.stopPropagation()}>
                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                    icon={<FaEye />} aria-label="Ver curso"
                                    onClick={() => setReading(course)} />
                                <CardActionMenu
                                    title="Descargar curso"
                                    ariaLabel={`Opciones de descarga de ${course.title || "curso"}`}
                                    items={[
                                        {
                                            key: "download-pdf",
                                            label: exportingId === course.id ? "Preparando PDF..." : "Descargar PDF completo",
                                            icon: <FaFileAlt />,
                                            isDisabled: !!exportingId,
                                            onClick: () => onExportCourse(course, "pdf"),
                                        },
                                        {
                                            key: "download-markdown",
                                            label: exportingId === course.id ? "Preparando Markdown..." : "Descargar Markdown por carpetas (.zip)",
                                            icon: <FaDownload />,
                                            isDisabled: !!exportingId,
                                            onClick: () => onExportCourse(course, "markdown"),
                                        },
                                    ]}
                                />
                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                    icon={course.isFavorite ? <FaStar /> : <FaRegStar />} aria-label="Favorito"
                                    onClick={() => onFavorite(course)} />
                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                    icon={<FaEdit />} aria-label="Administrar curso"
                                    onClick={() => navigate(`/courses/${course.id}/notebook`)} />
                                <AppButton size="sm" variant="ghost" tone="danger" isIconOnly
                                    icon={<FaArchive />} aria-label="Archivar curso"
                                    onClick={() => onArchive(course)} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ViewPagination
                page={PAGER.usePage}
                totalPages={PAGER.totalPages}
                total={PAGER.total}
                perPage={PAGER.usePerPage}
                onChange={PAGER.onChangePage}
            />

            <CourseReadModal
                isOpen={!!reading}
                onClose={() => setReading(null)}
                course={reading}
                courseId={reading?.id}
            />

            <CourseFormModal
                isOpen={isOpen}
                onClose={() => { setIsOpen(false); setEditing(null); }}
                course={editing}
                onSave={onSave}
                isLoading={useActionApiIsLoading}
            />
        </ViewLayout>
    );
};
